/**
 * ProcessViewer — 2-state process viewer container.
 *
 * State A (DAG): ProcessDAG fills available height. Click any node → NodeInfoBar
 *   slides up from bottom (fully expanded). "Open Tool" in NodeInfoBar → Tool view.
 *
 * State B (Tool): DAGStrip (thin h-14 strip) + NodeToolEmbed (flex-1) + ProcessTabBar.
 *   [← DAG] button or Escape → back to DAG view.
 *
 * (The HandoffChecklist right panel was retired with the HandoffPackage
 * path — requirement tracking now lives on the Spec panel, fed by
 * first-class SpecRequirements.)
 *
 * Spec panel: right-side slide-in (SpecPanel) showing the consumed Model
 * Specs for this BUILD workspace — sign-off badges + this build's scorecard.
 * The panel component stays mounted while visually closed so its fetch keeps
 * the toggle badge accurate (zero/pending states included). Re-fetch rides
 * projectStore.requirementsRefreshSignal (`requirements_updated` /
 * `validation_complete` workspace ops). NodeInfoBar requirement chips open
 * the panel focused on the clicked requirement; the requirement detail's
 * implementing-node button selects that node back in the canvas.
 *
 * Keyboard: ←/→ prev/next node, Escape back to DAG, Enter open tool.
 */

import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useProcessViewerStore } from '../../stores/processViewerStore';
import { useProjectStore } from '../../stores/projectStore';
import type { Cluster } from '../../types/cluster';
import ProcessDAG from './ProcessDAG';
import DAGStrip from './DAGStrip';
import NodeInfoBar from './NodeInfoBar';
import NodeToolEmbed from './NodeToolEmbed';
import ProcessTabBar from './ProcessTabBar';
import BuildRunControls from './BuildRunHeader';
import { type SpecPanelSummary } from '../production/SpecPanel';
import UnifiedFindingsPanel, { FindingsToggleButton } from '../workspace/UnifiedFindingsPanel';

const EMPTY_CLUSTER_RECORD: Record<string, Cluster> = {};

interface ProcessViewerProps {
  projectId: string;
  workspaceId?: string | null;
  authToken: string;
  userId?: string;
  googleWorkspaceEmail?: string | null;
}

interface ArtifactOpenTarget {
  nodeId: string;
  artifactName: string;
  artifactPath: string;
  /** True = artifactPath is a FOLDER to open directly (don't drop a filename).
   *  Used for INPUT_DATA nodes, which have no code — opening one shows the
   *  folder that houses the uploaded files, whether or not a file is there. */
  isFolder?: boolean;
}

export default function ProcessViewer({
  projectId,
  workspaceId,
  authToken,
  userId,
  googleWorkspaceEmail,
}: ProcessViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const clusters = useProjectStore((state) => state.dictWorkspace?.clusters ?? EMPTY_CLUSTER_RECORD);
  // Build-view drag persistence: saved cluster positions live on the project's
  // workspace_ui_state under a build-specific key (separate from the research
  // view's node_positions). Persisted the same way research positions are.
  const activeProject = useProjectStore((state) => state.activeProject);

  const {
    model,
    lastRun,
    modelLoadState,
    modelLoadError,
    viewState,
    selectedNodeId,
    topologicalOrder,
    activeRunProgress,
    activeRunId,
    pollingPhase,
    executionError,
    infoBarCollapse,
    loadFirstModel,
    clearSelection,
    selectNode,
    openTool,
    backToDAG,
    navigateToNextNode,
    navigateToPrevNode,
    setInfoBarCollapse,
    executeModel,
    stopPolling,
  } = useProcessViewerStore();

  // Load model on mount (no-op if already loaded for this (project, workspace))
  useEffect(() => {
    loadFirstModel(projectId, authToken, userId, workspaceId ?? undefined);
  }, [projectId, workspaceId, authToken, userId, loadFirstModel]);

  // ---------------------------------------------------------------------------
  // Spec panel (consumed Model Specs) — same shell pattern ProductionWorkspace
  // used: width-animated right container, panel mounted while closed so the
  // summary fetch keeps the toggle badge accurate. workspaceId is the BUILD
  // workspace (BuildArtifactsView passes dictWorkspace.workspace_id); authToken
  // is the Firebase ID token this view already uses for api.getModels.
  // ---------------------------------------------------------------------------
  const [isSpecPanelOpen, setIsSpecPanelOpen] = useState(false);
  const [isSpecDetailOpen, setIsSpecDetailOpen] = useState(false);
  const [specPanelWidth, setSpecPanelWidth] = useState(300);
  const [specSplitPercent, setSpecSplitPercent] = useState(55);
  const [specSummary, setSpecSummary] = useState<SpecPanelSummary | null>(null);
  // Controlled focus: NodeInfoBar requirement chips open the panel on a
  // specific `${source_workspace_id}::${requirement_id}` key.
  const [specFocusKey, setSpecFocusKey] = useState<string | null>(null);
  const [specFocusNonce, setSpecFocusNonce] = useState(0);
  const canShowSpecs = !!(projectId && workspaceId && authToken);
  // `requirements_updated` / `validation_complete` ops bump this counter in
  // projectStore.applyWorkspaceOperations → SpecPanel re-fetches.
  const requirementsRefreshSignal = useProjectStore((s) => s.requirementsRefreshSignal);
  // The build workspace's derived state feeds the unified linked-data panel
  // (findings / online / uploads / upstream OUTPUTS) alongside the consumed specs.
  const dictWorkspace = useProjectStore((s) => s.dictWorkspace);
  const [artifactOpenTarget, setArtifactOpenTarget] = useState<ArtifactOpenTarget | null>(null);

  const openSpecAtRequirement = useCallback((requirementKey: string) => {
    setSpecFocusKey(requirementKey);
    setSpecFocusNonce((n) => n + 1);
    setIsSpecPanelOpen(true);
  }, []);

  // BAR-40 (from main): stop the model-run poller when the viewer unmounts
  // (e.g. a phase switch within the same project, which does NOT trigger the
  // project-level resetStore). Without this, setInterval(poll, …) leaked until
  // its 90s timeout after the user navigated away. stopPolling() is idempotent.
  useEffect(() => {
    return () => { stopPolling(); };
  }, [stopPolling]);

  const handleOpenTool = useCallback((nodeId?: string) => {
    setArtifactOpenTarget(null);
    openTool(nodeId);
  }, [openTool]);

  const handleOpenArtifact = useCallback((nodeId: string, artifactName: string) => {
    const node = model?.nodes.find((n) => n.node_id === nodeId);
    // INPUT_DATA has no code — opening it ALWAYS opens the node's data folder in
    // Jupyter (where uploaded files land), whether or not a file is there yet.
    // Deterministic from model+node id; no run or upload metadata required.
    if (node?.node_type === 'input_data' && model?.model_id) {
      setArtifactOpenTarget({
        nodeId,
        artifactName,
        artifactPath: `artifacts/${model.model_id}/${nodeId}/current/`,
        isFolder: true,
      });
      openTool(nodeId);
      return;
    }
    // Any other node's output row opens the produced artifact's folder (post-run).
    const run = activeRunProgress || lastRun;
    const nodeResult = run?.node_results?.find((nr) => nr.node_id === nodeId);
    const artifactPath = nodeResult?.artifact_paths?.[artifactName];
    setArtifactOpenTarget(
      artifactPath
        ? { nodeId, artifactName, artifactPath, isFolder: false }
        : null
    );
    openTool(nodeId);
  }, [activeRunProgress, lastRun, openTool, model]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Don't capture keys when iframe is focused
      if (document.activeElement?.tagName === 'IFRAME') return;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          navigateToNextNode();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          navigateToPrevNode();
          break;
        case 'Escape':
          e.preventDefault();
          if (viewState === 'tool') {
            backToDAG();
          } else {
            clearSelection();
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedNodeId && viewState === 'dag') {
            handleOpenTool(selectedNodeId);
          }
          break;
      }
    },
    [viewState, selectedNodeId, navigateToNextNode, navigateToPrevNode, backToDAG, clearSelection, handleOpenTool],
  );

  // Cluster + node selection are mutually exclusive: selecting a cluster
  // (truthy) drops the selected node so the NodeInfoBar closes. The truthy
  // guard is load-bearing — ProcessDAG also fires this with null when it
  // switches to a node, and clearing then would wipe the node just selected.
  const handleClusterSelect = useCallback(
    (clusterId: string | null) => { if (clusterId) clearSelection(); },
    [clearSelection],
  );

  const buildPositions = activeProject?.workspace_ui_state?.build_node_positions;
  // Read the freshest project + ui_state from the store at call time (not the
  // render-time closure). Otherwise rapid drags spread a stale workspace_ui_state
  // and the second save clobbers the first (lost-update race).
  const handleBuildPositionsChange = useCallback(
    (positions: Record<string, { x: number; y: number }>) => {
      const { activeProject: project, updateWorkspace: update } = useProjectStore.getState();
      if (!project) return;
      update(project.workspace, {
        ...project.workspace_ui_state,
        build_node_positions: positions,
      });
    },
    [],
  );

  // Selected node object
  const selectedNode = model?.nodes.find((n) => n.node_id === selectedNodeId) || null;
  const selectedArtifactOpenTarget =
    selectedNode && artifactOpenTarget?.nodeId === selectedNode.node_id
      ? {
          artifactName: artifactOpenTarget.artifactName,
          artifactPath: artifactOpenTarget.artifactPath,
          isFolder: artifactOpenTarget.isFolder,
        }
      : null;

  // Topological position for prev/next
  const selectedIdx = selectedNodeId ? topologicalOrder.indexOf(selectedNodeId) : -1;
  const hasPrev = selectedIdx > 0;
  const hasNext = selectedIdx >= 0 && selectedIdx < topologicalOrder.length - 1;

  // ---------------------------------------------------------------------------
  // Loading / Error / Empty states
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Inner content — varies by load state
  // ---------------------------------------------------------------------------

  const innerContent = (() => {
    if (modelLoadState === 'loading') {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-fg-tertiary text-sm">Loading model...</p>
          </div>
        </div>
      );
    }

    if (modelLoadState === 'error') {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-sm">
            <h3 className="text-lg font-semibold text-danger mb-2">Failed to load model</h3>
            <p className="text-sm text-danger mb-4">{modelLoadError}</p>
            <button
              onClick={() => loadFirstModel(projectId, authToken, userId, workspaceId ?? undefined)}
              className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    if (modelLoadState === 'empty' || !model) {
      return (
        <div className="flex-1 flex items-center justify-center bg-bg-warm">
          <div className="text-center max-w-md">
            <svg className="w-16 h-16 mx-auto mb-4 text-fg-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
            <h2 className="text-lg font-semibold text-fg-secondary mb-2">No models in this project</h2>
            <p className="text-sm text-fg-tertiary mb-4">
              Models are created by the research agent during the build phase.
            </p>
            <p className="text-xs text-fg-tertiary">
              Try: &quot;Build an NMD behavioral model for deposit outflows&quot;
            </p>
          </div>
        </div>
      );
    }

    return null;
  })();

  return (
    <div
      ref={containerRef}
      className="h-full flex flex-col outline-none"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {/* Content row */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main area — relative so the spec toggle can float over it in
            every load state (the panel must be discoverable before a model
            exists, e.g. while upstream specs are still pending). */}
        <div className="flex-1 min-w-0 relative overflow-hidden flex">
        {innerContent ?? (
          /* Active model — 2-state view */
          model && viewState === 'dag' ? (
            /* State A: DAG view — run controls live in the canvas header
               (GraphCanvas headerActions), next to the legend; the workspace
               name lives in the WorkspaceBrief above, not here. */
            <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
              <div className="flex-1 min-w-0 relative overflow-hidden">
                <ProcessDAG
                  model={model}
                  clusters={clusters}
                  lastRun={lastRun}
                  activeRunProgress={activeRunProgress}
                  onNodeClick={(nodeId) => selectNode(nodeId)}
                  onNodeDoubleClick={(nodeId) => handleOpenTool(nodeId)}
                  onPaneClick={clearSelection}
                  selectedNodeId={selectedNodeId}
                  onClusterSelect={handleClusterSelect}
                  initialPositions={buildPositions}
                  onPositionsChange={handleBuildPositionsChange}
                  headerActions={
                    <BuildRunControls
                      projectId={projectId}
                      authToken={authToken}
                      userId={userId}
                      model={model}
                      lastRun={lastRun}
                      activeRunProgress={activeRunProgress}
                      activeRunId={activeRunId}
                      pollingPhase={pollingPhase}
                      executionError={executionError}
                      onRun={() => executeModel(projectId, model.model_id, authToken)}
                      onStop={stopPolling}
                    />
                  }
                />
                {selectedNode && model && (
                  <NodeInfoBar
                    node={selectedNode}
                    projectId={projectId}
                    modelId={model.model_id}
                    authToken={authToken}
                    userId={userId}
                    lastRun={lastRun}
                    activeRunProgress={activeRunProgress}
                    collapse={infoBarCollapse}
                    onCollapseChange={setInfoBarCollapse}
                    onOpenTool={() => handleOpenTool(selectedNode.node_id)}
                    onRequirementClick={canShowSpecs ? openSpecAtRequirement : undefined}
                    onSelectNode={selectNode}
                    onOpenArtifact={handleOpenArtifact}
                  />
                )}
              </div>
            </div>
          ) : model ? (
            /* State B: Tool view */
            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
              <DAGStrip
                model={model}
                topologicalOrder={topologicalOrder}
                selectedNodeId={selectedNodeId}
                lastRun={lastRun}
                activeRunProgress={activeRunProgress}
                onNodeSelect={selectNode}
                onBackToDAG={backToDAG}
              />

              <div className="flex-1 min-w-0 overflow-hidden">
                {selectedNode ? (
                  <NodeToolEmbed
                    node={selectedNode}
                    lastRun={lastRun}
                    activeRunProgress={activeRunProgress}
                    googleWorkspaceEmail={googleWorkspaceEmail}
                    authToken={authToken}
                    artifactOpenTarget={selectedArtifactOpenTarget}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-fg-tertiary text-sm">
                    Select a node to view its tool
                  </div>
                )}
              </div>

              {/* Tab bar — full width */}
              {selectedNode && (
                <ProcessTabBar
                  node={selectedNode}
                  onPrevNode={navigateToPrevNode}
                  onNextNode={navigateToNextNode}
                  hasPrev={hasPrev}
                  hasNext={hasNext}
                />
              )}
            </div>
          ) : null
        )}
        {/* Always discoverable once the fetch has resolved — a zero-spec
            build still shows the toggle so the panel can EXPLAIN why it's
            empty (upstream spec not committed yet / no sources wired),
            instead of the feature being invisible. */}
        {canShowSpecs && !isSpecPanelOpen && specSummary && (
          <FindingsToggleButton
            findingsCount={specSummary.openCount}
            onToggle={() => setIsSpecPanelOpen(true)}
          />
        )}
        </div>

        {/* Spec panel — right-hand slide container. The panel stays mounted
            while closed so its fetch keeps the toggle badge accurate; width 0
            hides it with the same slide transition the findings panel uses. */}
        {canShowSpecs && (
          <div
            className="h-full flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden"
            style={{
              width: isSpecPanelOpen
                ? isSpecDetailOpen
                  ? `${specSplitPercent}%`
                  : specPanelWidth
                : 0,
            }}
          >
            <UnifiedFindingsPanel
              allFindings={dictWorkspace?.findings || {}}
              allMarkers={dictWorkspace?.markers || {}}
              allOnlineSupport={dictWorkspace?.online_support || {}}
              allUploadedFiles={dictWorkspace?.uploaded_files || {}}
              linkedOutputs={dictWorkspace?.linked_outputs || []}
              selectedFindingId={null}
              isOpen={isSpecPanelOpen}
              workspaceId={workspaceId!}
              compartmentId={dictWorkspace?.compartment_id || 'default'}
              onToggle={() => setIsSpecPanelOpen((v) => !v)}
              onFindingSelect={() => {}}
              onClose={() => {
                setIsSpecPanelOpen(false);
                setIsSpecDetailOpen(false);
              }}
              onNavigateToNode={selectNode}
              onDetailOpenChange={setIsSpecDetailOpen}
              onPanelResize={(delta: number) => {
                if (isSpecDetailOpen) {
                  const containerWidth = containerRef.current?.offsetWidth || window.innerWidth;
                  const pctDelta = (delta / containerWidth) * 100;
                  setSpecSplitPercent((p) => Math.max(30, Math.min(75, p - pctDelta)));
                } else {
                  setSpecPanelWidth((w) => Math.max(220, Math.min(520, w - delta)));
                }
              }}
              /* Spec-mode: fold the former SpecPanel in. */
              specProjectId={projectId}
              authToken={authToken}
              specRefreshSignal={requirementsRefreshSignal}
              specFocusKey={specFocusKey}
              specFocusNonce={specFocusNonce}
              onSpecSummaryChange={setSpecSummary}
            />
          </div>
        )}
      </div>
    </div>
  );
}
